/* eslint-disable no-useless-concat */
/* eslint-disable prefer-template */
/* eslint-disable max-len */
/* eslint-disable jsx-a11y/anchor-is-valid */
/**
@format
@flow
*/

import React, { useEffect, useReducer } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { InView } from 'react-intersection-observer';
import {
  useDispatch, useSelector, shallowEqual,
} from 'react-redux';
import {
  withAppManager, LoadingSpinner, formatDate,
} from '@ziynx/components/utilities';
import Colors from '@ziynx/theme/colors';
import { Heading, Body } from '@ziynx/components/typography';
import { ContentContainer, PageTemplate } from '@ziynx/components/layout';
import Headline from '@ziynx/components/headlines';
import Card from '@ziynx/components/cards';
import Icon from '@ziynx/components/icons';
import Link from '@ziynx/components/links';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import type { Viewports } from '@ziynx/flow-types/globals';
import ActionTypes from '@ziynx/action-types';
import { eventNames } from '@ziynx/analytics';
import * as KrispyTokens from '@krispy-tokens/blue';
import DestructiveModal from '../Modals/DestructiveModal';

type Props = $ReadOnly<{|
  viewport: Viewports,
  /**
   * Navigation stack
   */
  history: Object,
|}>;

type DispatchAction = {
  type: string,
} => void;

const StyledHeadline = styled(Headline)`
  margin-bottom: ${KrispyTokens.KrispySpacingElementsLdXsmall}px;
`;

const QuestionsWrapper = styled.div`
  ${({ isDesktop }) => isDesktop && `margin-top: ${KrispyTokens.KrispySpacingElementsLdXsmall}px;`}
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: ${KrispyTokens.KrispySpacingElementsLdXsmall}px;
`;

const StyledCard = styled(Card)`
  margin-bottom: ${KrispyTokens.KrispySpacingElementsMdMedium}px;
`;

const EmptyWrapper = styled.div`
  text-align: center;
`;

const IconWrapper = styled.div`
  margin-bottom: ${KrispyTokens.KrispySpacingElementsHdXlarge}px;
`;

const TextWrapper = styled.div`
  margin-bottom: ${KrispyTokens.KrispySpacingElementsHdSmall}px;
`;

function reducer(state: ReducerState, action: ActionType) {
  const { type } = action;

  switch (type) {
  case 'setDestructiveModal': {
    return {
      ...state,
      destructiveModal: action?.destructiveModal,
    };
  }
  case 'setCurrentPage': {
    return {
      ...state,
      currentPage: action?.currentPage,
    };
  }
  default: {
    return state;
  }
  }
}

const initialReducerState: ReducerState = {
  destructiveModal: {
    open: false,
  },
  currentPage: undefined,
};

const NILListScreen = (props: ManagedProps<Props>): React$Node => {
  const { viewport, history } = props;

  const isMobileSmall = viewport === 'mobileSmall';

  const dispatch: DispatchAction = useDispatch();
  const [state, dispatchReducer] = useReducer(reducer, initialReducerState);
  const NILService = useSelector(({ NILList }) => NILList || {}, shallowEqual);
  useEffect(() => {
    /** Need an initial fetch to get changes from edit/update/post
     * Then set the number of pages that need to be fetched
     */
    if (state?.currentPage === undefined) {
      dispatch({
        type: ActionTypes.NIL.getAll.START,
        page: 0,
      });
      dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames.navigated_nil_list,
      });
      dispatchReducer({
        type: 'setCurrentPage',
        currentPage: NILService?.page,
      });
    }
  }, []);
  useEffect(() => {
    if (NILService.fetching || state.currentPage === undefined) return;
    if (state.currentPage > NILService.page) {
      const nextPage = NILService.page + 1;
      dispatch({
        type: ActionTypes.NIL.getAll.START,
        page: nextPage,
      });
      dispatchReducer({
        type: 'setCurrentPage',
        currentPage: state.currentPage,
      });
    }
  }, [NILService.fetching]);
  const _onPaginate = (inView): void => {
    const { page, isLastPage } = NILService;
    if (isLastPage || (NILService?.fetching || !inView)) return;
    const nextPage = page + 1;
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.scrolled_through_nil_list,
      properties: {
        pageNumber: nextPage,
      },
    });
    dispatch({
      type: ActionTypes.NIL.getAll.START,
      page: nextPage,
    });
    dispatchReducer({
      type: 'setCurrentPage',
      currentPage: nextPage,
    });
  };

  const _renderEmpty = () => (
    <EmptyWrapper>
      <IconWrapper>
        <Icon name="post-fill" small color={Colors.GrayLight} />
      </IconWrapper>
      <TextWrapper>
        <Heading medium color={Colors.Gray}>No NIL</Heading>
      </TextWrapper>
      {!(NILService?.results ?? []).length
      && (
        <Body medium>
          <Link
            large
            variant="text"
            onClick={() => {
              // eslint-disable-next-line react/prop-types
              history.push('/nil/create', { from: 'NILList' });
            }}
          >
            Add new
          </Link>
        </Body>
      )}
    </EmptyWrapper>
  );

  const _renderNILCards = () => NILService?.results?.map((item, index): React$Node => {
    if (!item) return null;
    // eslint-disable-next-line prefer-template
    const editNILPath = '/nil/' + item?._id + '/edit';

    // eslint-disable-next-line prefer-template
    let postDate;
    if (item?.endDate) {
      const currentTime = new Date();
      const endDate = new Date(item?.endDate);
      const timeDifference = currentTime.getTime() - endDate.getTime();
      const isPast = timeDifference > 0;
      postDate = (isPast ? 'Ended ' : 'Ends in ') + formatDate(item?.endDate);
      if (postDate.indexOf('left') > 0) {
        // eslint-disable-next-line prefer-destructuring
        postDate = postDate.split(' left', 2)[0];
      }
    }

    const _deletePost = (postId) => {
      dispatch({
        type: ActionTypes.NIL.delete.START,
        id: item?._id,
      });
      dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames.deleted_nil_post,
        properties: {
          postId,
        },
      });
      dispatchReducer({
        type: 'setDestructiveModal',
        destructiveModal: {
          open: false,
        },
      });
    };

    const headingFinal = item?.status === 'open' ? 'Are you sure that you would like to close this NIL Application?' : 'Are you sure that you would like to open this NIL Application?';

    const thumbnailImage = item?.thumbnail?.sources?.cropped?.url ?? item?.thumbnail?.sources?.original?.url;

    return (
      <StyledCard
        key={index}
        variant={thumbnailImage ? 'videoPreview' : 'squircle'}
        image={{ imageSource: thumbnailImage }}
        squircle={{
          iconName: 'post-fill',
          color: Colors.White,
          backgroundColor: Colors.GrayVeryDark,
          inverted: false,
        }}
        title={item?.title}
        subtitle={item?.applicationCount + ' applications'}
        subtitleColor={Colors.GrayVeryDark}
        body={postDate}
        archived={item?.archived}
        viewport={viewport}
        rightAction={{
          popover: {
            position: 'bottomLeft',
            links: [
              {
                children: 'View Applications',
                large: true,
                onClick: () => {
                  history.push('/nil/' + item?._id + '/applications', { from: 'NILList', postTitle: item?.title });
                },
              },
              {
                children: 'Edit NIL Post',
                large: true,
                onClick: () => { history.push(editNILPath, { from: 'NILList' }); },
              },
              {
                children: item?.status === 'open' ? 'Close NIL post' : 'Open NIL post',
                large: true,
                color: Colors.SecondaryRed,
                onClick: () => {
                  dispatchReducer({
                    type: 'setDestructiveModal',
                    destructiveModal: {
                      open: true,
                      heading: headingFinal,
                      buttonGroup: {
                        destructive: {
                          children: item?.status === 'open' ? 'Close' : 'Open',
                          onClick: () => {
                            dispatch({
                              type: ActionTypes.NIL.patch.START,
                              id: item?._id,
                              body: [{
                                op: 'replace',
                                path: 'status',
                                value: item?.status === 'open' ? 'closed' : 'open',
                              }],
                            });
                            dispatchReducer({
                              type: 'setDestructiveModal',
                              destructiveModal: {
                                open: false,
                              },
                            });
                          },
                        },
                        primary: {
                          children: 'Cancel',
                          onClick: () => {
                            dispatchReducer({
                              type: 'setDestructiveModal',
                              destructiveModal: {
                                open: false,
                              },
                            });
                          },
                        },
                      },
                    },
                  });
                },
              },
              {
                children: 'Delete NIL post',
                large: true,
                color: Colors.SecondaryRed,
                onClick: () => {
                  // Don't like this but oh well
                  dispatchReducer({
                    type: 'setDestructiveModal',
                    destructiveModal: {
                      open: true,
                      heading: 'Are you sure that you would like to delete this NIL Application?',
                      buttonGroup: {
                        destructive: {
                          children: 'Delete',
                          onClick: () => _deletePost(item?._id),
                        },
                        primary: {
                          children: 'Cancel',
                          onClick: () => {
                            dispatchReducer({
                              type: 'setDestructiveModal',
                              destructiveModal: {
                                open: false,
                              },
                            });
                          },
                        },
                      },
                    },
                  });
                },
              },
            ],
          },
        }}
      />
    );
  });

  const _renderLeftContent = () => {
    if (!(NILService?.results ?? []).length
    && (!NILService?.fetching)
    ) {
      return <Card variant="list" renderEmpty={_renderEmpty} />;
    }

    return (
      <>
        { _renderNILCards() }
        <InView as="div" onChange={_onPaginate}>
          <SpinnerWrapper>
            {!NILService?.isLastPage && NILService?.fetching && <LoadingSpinner />}
          </SpinnerWrapper>
        </InView>
      </>
    );
  };

  const leftSlot = (): React$Node => (
    <>
      <StyledHeadline
        variant="page"
        title={{ text: 'NIL' }}
        squircleButton={isMobileSmall && {
          icon: { size: 'large' },
          onClick: () => history.push('/nil/create', { from: 'NILList' }),
        }}
        textLink={!isMobileSmall && {
          large: true,
          children: 'Add new',
          onClick: () => history.push('/nil/create', { from: 'NILList' }),
        }}
      />
    </>
  );

  const leftContent = (): React$Node => (
    <QuestionsWrapper isDesktop={viewport === 'desktop'}>
      {_renderLeftContent()}
      {NILService?.fetching && !(NILService?.results ?? []).length && (
        <SpinnerWrapper>
          <LoadingSpinner />
        </SpinnerWrapper>
      )}
    </QuestionsWrapper>
  );

  const rightSlot = (): React$Node => (
    <>
      {/* <Headline
        variant="default"
        title={{ text: 'Filter' }}
        style={{ marginBottom: '24px' }}
      />
      <ButtonPillGroup stacked pills={filterButtons} /> */}
    </>
  );

  return (
    <>
      <Helmet>
        <title>NIL | ZIYNX</title>
      </Helmet>
      <DestructiveModal
        open={state?.destructiveModal?.open}
        heading={{ text: state?.destructiveModal?.heading || '' }}
        buttonGroup={state?.destructiveModal?.buttonGroup}
      />
      <ContentContainer offsetTop={86}>
        <PageTemplate
          alwaysShowRightSlot
          viewport={viewport}
          leftSlot={leftSlot()}
          leftContent={leftContent()}
          rightSlot={rightSlot()}
          headerOffset={-1000} // Temporary measure to remove barSticky
        />
      </ContentContainer>
    </>
  );
};

export default (withAppManager(NILListScreen): React$ComponentType<ManagedProps<Props>>);
